// Typography primitives for the design system. Each maps to a fluid scale
// class in globals.css. Soft by default (regular weight); pass `bold` for the
// rare emphasis, or `as` to change the rendered element without changing size.

import React from "react";

type Variant =
  | "display"
  | "h1"
  | "h2"
  | "h3"
  | "title"
  | "lead"
  | "body"
  | "sm"
  | "label"
  | "mono";

const CLASS: Record<Variant, string> = {
  display: "t-display",
  h1: "t-h1",
  h2: "t-h2",
  h3: "t-h3",
  title: "t-title",
  lead: "t-lead",
  body: "t-body",
  sm: "t-sm",
  label: "t-label",
  mono: "t-mono",
};

const DEFAULT_TAG: Record<Variant, keyof React.JSX.IntrinsicElements> = {
  display: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  title: "h3",
  lead: "p",
  body: "p",
  sm: "p",
  label: "span",
  mono: "code",
};

type TextProps = {
  variant?: Variant;
  as?: keyof React.JSX.IntrinsicElements;
  bold?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "style" | "className">;

export function Text({ variant = "body", as, bold, className, style, children, ...rest }: TextProps) {
  const Tag = (as ?? DEFAULT_TAG[variant]) as React.ElementType;
  return (
    <Tag
      className={[CLASS[variant], className].filter(Boolean).join(" ")}
      style={{ ...(bold ? { fontWeight: 700 } : null), ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

type V = Omit<TextProps, "variant">;
export const Display = (p: V) => <Text variant="display" {...p} />;
export const H1 = (p: V) => <Text variant="h1" {...p} />;
export const H2 = (p: V) => <Text variant="h2" {...p} />;
export const H3 = (p: V) => <Text variant="h3" {...p} />;
export const Title = (p: V) => <Text variant="title" {...p} />;
export const Lead = (p: V) => <Text variant="lead" {...p} />;
export const Body = (p: V) => <Text variant="body" {...p} />;
export const Small = (p: V) => <Text variant="sm" {...p} />;
export const Label = (p: V) => <Text variant="label" {...p} />;
export const Mono = (p: V) => <Text variant="mono" as="code" {...p} />;
